/**
 * Experiment Configuration Store
 * Manages experiment mode, student progress, and access control
 */

import { create } from 'zustand';

type ExperimentMode = 'practice' | 'test';
type ProgressStatus = 'not-started' | 'pre-lab' | 'in-lab' | 'post-lab' | 'completed' | 'teacher-bypass';

interface ExperimentConfig {
    id: string;
    experimentType: 'titration' | 'znH2SO4';
    classCode: string;
    teacherId: string;
    
    // Pre-lab settings
    preLab: {
        enabled: boolean;
        passingScore: number;
        maxAttempts: number;
        showAnswersAfterFail: boolean;
    };
    
    // Experiment mode
    experimentMode: ExperimentMode;
    autoFillData: boolean;
    allowStudentConfig?: boolean;
    
    // Post-lab settings
    postLab: {
        enabled: boolean;
    };
    
    // Access control
    allowTeacherBypass: boolean;
}

interface StudentProgress {
    id: string;
    studentId: string;
    experimentConfigId: string;
    
    preLab: {
        attempts: number;
        score: number;
        passed: boolean;
        completedAt?: string;
    };
    
    labData: {
        startedAt?: string;
        completedAt?: string;
        dataPoints: any[];
        manualEntry: boolean;
    };
    
    postLab: {
        answers: { questionId: string; answer: string }[];
        submittedAt?: string;
        teacherFeedback?: string;
        grade?: number;
    };
    
    status: ProgressStatus;
}

interface ExperimentConfigState {
    // Current experiment configuration
    currentConfig: ExperimentConfig | null;
    setCurrentConfig: (config: ExperimentConfig | null) => void;
    
    // Student progress
    studentProgress: StudentProgress | null;
    setStudentProgress: (progress: StudentProgress | null) => void;
    
    // Mode and access
    isTeacherMode: boolean;
    setTeacherMode: (isTeacher: boolean) => void;
    
    // Auto-fill control
    shouldAutoFill: () => boolean;
    
    // Progress checks
    canAccessLab: () => boolean;
    canAccessDiscussion: () => boolean;
    
    // Status helpers
    getNextStep: () => 'pre-lab' | 'lab' | 'discussion' | 'completed';
    updateStatus: (status: ProgressStatus) => void;
}

export const useExperimentConfig = create<ExperimentConfigState>((set, get) => ({
    currentConfig: null,
    studentProgress: null,
    isTeacherMode: false,
    
    setCurrentConfig: (config) => set({ currentConfig: config }),
    setStudentProgress: (progress) => set({ studentProgress: progress }),
    setTeacherMode: (isTeacher) => set({ isTeacherMode: isTeacher }),
    
    // Determine if data should auto-fill
    shouldAutoFill: () => {
        const { currentConfig, isTeacherMode } = get();
        
        // Teacher mode always auto-fills for testing
        if (isTeacherMode) return true;
        
        // Practice mode auto-fills (also default to practice if no config is loaded yet)
        if (!currentConfig || currentConfig.experimentMode === 'practice') return true;
        
        // Test mode requires manual entry
        return false;
    },
    
    // Check if student can access lab
    canAccessLab: () => {
        const { studentProgress, isTeacherMode, currentConfig } = get();
        
        // Teacher can always access
        if (isTeacherMode) return true;
        
        // If teacher bypassed, allow access
        if (studentProgress?.status === 'teacher-bypass') return true;
        
        // If pre-lab is disabled, allow access
        if (!currentConfig?.preLab.enabled) return true;
        
        // Otherwise, must pass pre-lab
        return studentProgress?.preLab.passed || false;
    },
    
    // Check if student can access discussion
    canAccessDiscussion: () => {
        const { studentProgress, isTeacherMode, currentConfig } = get();
        
        // Teacher can always access
        if (isTeacherMode) return true;
        
        // If discussion is disabled, no access needed
        if (!currentConfig?.postLab.enabled) return false;
        
        // Must complete lab first
        return studentProgress?.labData.completedAt !== undefined;
    },
    
    // Get next step in workflow
    getNextStep: () => {
        const { studentProgress, currentConfig, canAccessLab, canAccessDiscussion } = get();
        
        if (!studentProgress) return 'pre-lab';
        
        // Check pre-lab
        if (currentConfig?.preLab.enabled && !canAccessLab()) {
            return 'pre-lab';
        }
        
        // Check lab
        if (!studentProgress.labData.completedAt) {
            return 'lab';
        }
        
        // Check discussion
        if (currentConfig?.postLab.enabled && canAccessDiscussion() && !studentProgress.postLab.submittedAt) {
            return 'discussion';
        }
        
        return 'completed';
    },
    
    // Update student status
    updateStatus: (status) => {
        set((state) => ({
            studentProgress: state.studentProgress
                ? { ...state.studentProgress, status }
                : null,
        }));
    },
}));

// Helper hook for mode-aware components
export function useExperimentMode() {
    const shouldAutoFill = useExperimentConfig((state) => state.shouldAutoFill());
    const currentConfig = useExperimentConfig((state) => state.currentConfig);
    const isTeacherMode = useExperimentConfig((state) => state.isTeacherMode);
    
    return {
        mode: currentConfig?.experimentMode || 'practice',
        isAutoFill: shouldAutoFill,
        isTeacherMode,
        isPracticeMode: currentConfig?.experimentMode === 'practice',
        isTestMode: currentConfig?.experimentMode === 'test',
    };
}

// Helper hook for progress tracking
export function useStudentProgress() {
    const progress = useExperimentConfig((state) => state.studentProgress);
    const getNextStep = useExperimentConfig((state) => state.getNextStep);
    const canAccessLab = useExperimentConfig((state) => state.canAccessLab);
    const canAccessDiscussion = useExperimentConfig((state) => state.canAccessDiscussion);
    
    return {
        progress,
        nextStep: getNextStep(),
        canAccessLab: canAccessLab(),
        canAccessDiscussion: canAccessDiscussion(),
        isCompleted: progress?.status === 'completed',
    };
}
